<script setup lang="ts">
import {computed} from "vue";
import {IUserValueScalesReportData} from "@/pages/Insights/ReportsDetailPage/reportTypes";

defineProps<{
  reportData: IUserValueScalesReportData[];
  loadingData: boolean;
}>();

const headers = computed(
  () => [
    {
      title: "User",
      key: "fullName"
    },
    {
      title: "Tenant",
      key: "tenant"
    },
    {
      title: "Self Transcendence",
      key: "results.self_transcendence"
    },
    {
      title: "Conservation",
      key: "results.conservation"
    },
    {
      title: "Self Enhancement",
      key: "results.self_enhancement"
    },
    {
      title: "Openness to Change",
      key: "results.openness_to_change"
    },
  ]
);

function getLevelColor(percentage: number) {
  if (percentage > 90)
    return "green-darken-3";

  if (percentage > 75)
    return "green";

  if (percentage > 60)
    return "light-green";

  if (percentage > 45)
    return "orange";

  if (percentage > 30)
    return "light-orange";

  return "red";
}

function getLevelLabel(percentage: number) {
  if (percentage > 90)
    return "Very High";

  if (percentage > 75)
    return "High";

  if (percentage > 60)
    return "Medium";

  if (percentage > 45)
    return "Low";

  return "Very Low";
}

</script>

<template>
  <v-data-table
    :items="reportData"
    :headers="headers"
    :loading="loadingData"
    :items-per-page="50"
  >
    <template v-slot:item.fullName="{ item }">
      <span class="font-weight-bold">{{ item.fullName }}</span>

      <div>
        <div v-if="item.email">
          <v-icon size="x-small">far fa-envelope</v-icon>
          {{ item.email }}
        </div>
        <div v-if="item.phone">
          <v-icon size="x-small">far fa-phone</v-icon>
          {{ item.phone }}
        </div>
      </div>
    </template>

    <template v-slot:item.tenant="{ item }">
      <div class="py-4">
        <div class="font-weight-bold text-blue-grey-darken-1 pb-1">{{ item.tenant.title }}</div>
        <div v-if="item.cohort.title || item.institution.title">
          <v-chip v-if="item.institution.title" size="small" class="font-weight-bold mr-3">
            <v-icon color="grey" class="mr-2">far fa-building</v-icon>
            {{ item.institution.title }}
          </v-chip>
          <v-chip v-if="item.cohort.title" size="small" class="font-weight-bold">
            <v-icon color="grey" class="mr-2">far fa-graduation-cap</v-icon>
            {{ item.cohort.title }}
          </v-chip>
        </div>
      </div>
    </template>

    <template v-slot:[`item.results.self_transcendence`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results.self_transcendence" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <v-progress-linear color="green" :model-value="item.results.self_transcendence"/>

          <v-chip :color='getLevelColor(item.results.self_transcendence as number)' size="x-small"
                  class="font-weight-bold mt-2">
            {{getLevelLabel(item.results.self_transcendence as number) }} {{ item.results.self_transcendence }}%
          </v-chip>
        </div>
      </div>
    </template>
    <template v-slot:[`item.results.conservation`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results.conservation" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <v-progress-linear color="orange" :model-value="item.results.conservation"/>
          <v-chip :color='getLevelColor(item.results.conservation as number)' size="x-small"
                  class="font-weight-bold mt-2">
            {{getLevelLabel(item.results.conservation as number) }} {{ item.results.conservation }}%
          </v-chip>
        </div>
      </div>
    </template>
    <template v-slot:[`item.results.self_enhancement`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results.self_enhancement" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <v-progress-linear color="blue" :model-value="item.results.self_enhancement"/>
          <v-chip :color='getLevelColor(item.results.self_enhancement as number)' size="x-small"
                  class="font-weight-bold mt-2">
            {{getLevelLabel(item.results.self_enhancement as number) }} {{ item.results.self_enhancement }}%
          </v-chip>
        </div>
      </div>
    </template>
    <template v-slot:[`item.results.openness_to_change`]="{ item }">
      <div class="text-center">
        <v-chip v-if="!item.results.openness_to_change" size="x-small">No Results</v-chip>
        <div v-else class="text-center">
          <v-progress-linear color="purple" :model-value="item.results.openness_to_change"/>
          <v-chip :color='getLevelColor(item.results.openness_to_change as number)' size="x-small"
                  class="font-weight-bold mt-2">
            {{getLevelLabel(item.results.openness_to_change as number) }} {{ item.results.openness_to_change }}%
          </v-chip>
        </div>
      </div>
    </template>

  </v-data-table>
</template>

<style scoped>

</style>
